import { useState, useRef, useEffect, useCallback } from 'react';
import type { GraphUser } from '../types';
import { useApiClient } from '../api/client';

interface PeoplePickerProps {
  /** Called when user selects a result */
  onSelect: (user: GraphUser) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Clear the input after selection? (default true) */
  clearOnSelect?: boolean;
  id?: string;
}

export default function PeoplePicker({
  onSelect,
  placeholder = 'Search by name or email…',
  disabled = false,
  clearOnSelect = true,
  id,
}: PeoplePickerProps) {
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState<GraphUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]       = useState(false);
  const timerRef              = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef               = useRef<HTMLDivElement>(null);
  const api                   = useApiClient();

  const search = useCallback(async (q: string) => {
    if (!q || q.length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    setOpen(true);
    try {
      const data = await api.get<GraphUser[]>(`/users/search?q=${encodeURIComponent(q)}`);
      setResults(Array.isArray(data) ? data : []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  function handleInput(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    setQuery(val);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => search(val), 300);
  }

  function handleSelect(user: GraphUser) {
    onSelect(user);
    if (clearOnSelect) setQuery('');
    setOpen(false);
    setResults([]);
  }

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="autocomplete" ref={wrapRef}>
      <input
        id={id}
        type="text"
        className="form-control"
        placeholder={placeholder}
        value={query}
        onChange={handleInput}
        onFocus={() => query.length >= 2 && setOpen(true)}
        disabled={disabled}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={open}
      />
      {open && (
        <ul className="autocomplete-results" role="listbox">
          {loading && <li className="result-item">Searching…</li>}
          {!loading && results.length === 0 && query.length >= 2 && (
            <li className="no-result">No users found</li>
          )}
          {results.map(u => (
            <li
              key={u.id}
              className="result-item"
              role="option"
              aria-selected={false}
              onMouseDown={() => handleSelect(u)}
            >
              <div className="user-item">
                <div className="avatar-circle">
                  {u.displayName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <strong>{u.displayName}</strong>
                  <br />
                  <small>{u.userPrincipalName}</small>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
